// src/features/dashboard/components/EmployeeCompliance/ComplianceHoursChart.tsx
import { useState } from "react";
import { CalendarCheck2 } from "lucide-react";

import {
  ResponsiveContainer,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  BarChart,
  Bar,
  Cell,
  ReferenceLine,
  LabelList,
  Legend,
} from "recharts";

import {
  HoursChartItem,
  HoursTableItem,
} from "../../types/Employee/employeeCompliance.types";

interface Props {
  data: HoursChartItem[];
  tableData: HoursTableItem[];
}

const LOGGED_COLOR = "#7C3AED";

const REQUIRED_COLOR = "#D1D5DB";


const ComplianceHoursChart = ({
  data,
  tableData,
}: Props) => {

  const [hovered, setHovered] = useState<{
  index: number | null;
  key: "loggedHours" | "requiredHours" | null;
}>({
  index: null,
  key: null,
});

const [hoveredLegend, setHoveredLegend] = useState<
  "loggedHours" | "requiredHours" | null
>(null);

const requiredHours =
  data.length > 0 ? data[0].requiredHours : 8;

  // Dynamic X-axis labels
const chartData = data.map((item) => ({
  ...item,
  shortDay: (() => {
    const parts = item.day.split(" ");

    if (parts.length >= 2) {
      // Monday 04 -> Mon 04
      return `${parts[0].substring(0, 3)} ${parts[1]}`;
    }

    return item.day.substring(0, 3);
  })(),
}));

// ------------------------------
// Dynamic Y-axis
// ------------------------------

const values = [
  ...data.map((x) => Number(x.loggedHours)),
  ...data.map((x) => Number(x.requiredHours)),
].filter((value) => Number.isFinite(value));

const minValue = values.length
  ? Math.min(...values)
  : 8;

const maxValue = values.length
  ? Math.max(...values)
  : 8;

// Keep some space above the highest value
const yMin = Math.max(
  0,
  Math.floor(minValue - 1)
);

const yMax = Math.ceil(maxValue + 1);

// Generate ticks every 2 hours
const ticks: number[] = [];

const firstTick = Math.floor(yMin / 2) * 2;
const lastTick = Math.ceil(yMax / 2) * 2;

for (let i = firstTick; i <= lastTick; i += 2) {
  ticks.push(i);
}

console.log({
  values,
  minValue,
  maxValue,
  yMin,
  yMax,
  ticks,
});

  return (
    <div
      className="
        rounded-2xl
        border
        border-[#D9DEE8]
        bg-white
        p-5
      "
    >
      {/* Header */}

      <div className="mb-5 flex items-center justify-between">

        <div className="flex items-center gap-2">

          <div className="flex h-5 w-5 items-center justify-center rounded-full bg-[#0F3D75]">

            <CalendarCheck2
              size={11}
              color="#FFFFFF"
              strokeWidth={2.5}
            />

          </div>

          <h3 className="text-[15px] font-semibold text-[#002B5B]">
            Gate 3 · Hours Requirement
          </h3>

        </div>

       <div
  className="
    rounded-full
    bg-[#EAF2FF]
    px-3
    py-[5px]
    text-[12px]
    font-medium
    text-[#2563EB]
  "
>
  Min {requiredHours}h logged per shift · WTD performance
</div>

      </div>

            {/* Legend */}

      <div className="mb-4 flex items-center justify-center gap-6">

  <div
    className="flex cursor-pointer items-center gap-2"
    onMouseEnter={() => setHoveredLegend("loggedHours")}
    onMouseLeave={() => setHoveredLegend(null)}
  >
    <span
      className="h-3 w-3 rounded-[2px] transition-all"
      style={{
        backgroundColor: LOGGED_COLOR,
        opacity:
          hoveredLegend === null
            ? 1
            : hoveredLegend === "loggedHours"
            ? 1
            : 0.3,
      }}
    />

    <span
      className={`text-[13px] transition-all ${
        hoveredLegend === "loggedHours"
          ? "font-semibold text-[#111827]"
          : "text-[#334155]"
      }`}
    >
      Logged
    </span>
  </div>

  <div
    className="flex cursor-pointer items-center gap-2"
    onMouseEnter={() => setHoveredLegend("requiredHours")}
    onMouseLeave={() => setHoveredLegend(null)}
  >
    <span
      className="h-3 w-3 rounded-[2px] transition-all"
      style={{
        backgroundColor: REQUIRED_COLOR,
        opacity:
          hoveredLegend === null
            ? 1
            : hoveredLegend === "requiredHours"
            ? 1
            : 0.3,
      }}
    />

    <span
      className={`text-[13px] transition-all ${
        hoveredLegend === "requiredHours"
          ? "font-semibold text-[#111827]"
          : "text-[#334155]"
      }`}
    >
      Min Req
    </span>
  </div>

</div>


            {/* Chart */}

      <div className="h-[280px]">

       <ResponsiveContainer
  width="100%"
  height={250}
>
  <BarChart
  data={chartData}
   margin={{
  top: 10,
  right: 20,
  left: 20,
  bottom: 0,
}}
    barGap={2}
    barCategoryGap="30%"
  >
    <CartesianGrid
      stroke="#EEF2F7"
      vertical={false}
    />

    <XAxis
  dataKey="shortDay"
  axisLine={false}
  tickLine={false}
  tick={{
    fontSize: 12,
    fill: "#334155",
  }}
/>

   <YAxis
  domain={[yMin, yMax]}
  ticks={ticks}
  interval={0}
  allowDataOverflow={false}
  width={55}
  axisLine={false}
  tickLine={false}
  tick={{
    fontSize: 12,
    fill: "#334155",
  }}
  tickFormatter={(value) => `${value}h`}
  label={{
    value: "Hours",
    angle: -90,
    position: "insideLeft",
    dx: -18,
    dy: 10,
    style: {
      fill: "#111827",
      fontSize: 12,
      fontWeight: 600,
      textAnchor: "middle",
    },
  }}
/>

    <Tooltip
  cursor={false}
  content={({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const current = payload.find(
      (item: any) => item.dataKey === hovered.key
    );

    if (!current) return null;

    const item = current.payload;

    return (
      <div className="rounded-lg border border-[#D0D5DD] bg-white px-4 py-3 shadow-lg">
        <div className="mb-2 text-[13px] font-semibold text-[#344054]">
          {label}
        </div>

        <div className="flex items-center gap-2">
          <span
            className="h-3 w-3 rounded-full"
            style={{
              backgroundColor:
                current.dataKey === "loggedHours"
                  ? "#7C3AED"
                  : "#E5E7EB",
            }}
          />

          <span className="text-[13px] text-[#344054]">
            {current.dataKey === "loggedHours"
              ? "Logged:"
              : "Minimum:"}
          </span>

          <span className="text-[13px] font-semibold text-[#111827]">
            {current.dataKey === "loggedHours"
              ? item.loggedHoursFormatted
              : `${item.requiredHours}h`}
          </span>
        </div>

        {current.dataKey === "loggedHours" && (
          <div className="mt-2 text-[12px] text-[#667085]">
            Status: {item.status}
          </div>
        )}
      </div>
    );
  }}
/>

  <ReferenceLine
  y={requiredHours}
  stroke="#F87171"
  strokeDasharray="4 4"
  strokeWidth={1.5}
  label={({ viewBox }) => {
    if (!viewBox) return null;

    const x = viewBox.x + viewBox.width - 58;
    const y = viewBox.y - 20;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={54}
          height={18}
          rx={3}
          ry={3}
          fill="#FFFFFF"
          stroke="#EF4444"
          strokeWidth={1}
        />

        <text
          x={x + 27}
          y={y + 12}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#EF4444"
          fontSize={11}
          fontWeight={600}
        >
          {requiredHours}h min
        </text>
      </g>
    );
  }}
/>

   <Bar
  dataKey="loggedHours"
  name="Logged"
  radius={[3, 3, 0, 0]}
  barSize={20}
  onMouseOver={(_, index) =>
    setHovered({
      index,
      key: "loggedHours",
    })
  }
  onMouseOut={() =>
    setHovered({
      index: null,
      key: null,
    })
  }
>
  {data.map((_, index) => (
    <Cell
      key={index}
     fill="#7C3AED"
fillOpacity={
  hoveredLegend === null
    ? 1
    : hoveredLegend === "loggedHours"
    ? 1
    : 0.25
}
    />
  ))}
</Bar>

<Bar
  dataKey="requiredHours"
  name="Min Req"
  radius={[3, 3, 0, 0]}
  barSize={20}
  onMouseOver={(_, index) =>
    setHovered({
      index,
      key: "requiredHours",
    })
  }
  onMouseOut={() =>
    setHovered({
      index: null,
      key: null,
    })
  }
>
  {data.map((_, index) => (
    <Cell
      key={index}
      fill="#E5E7EB"
fillOpacity={
  hoveredLegend === null
    ? 1
    : hoveredLegend === "requiredHours"
    ? 1
    : 0.25
}
    />
  ))}
</Bar>

  </BarChart>
</ResponsiveContainer>

      </div>

      {/* Part 2 - Table starts here */}

            {/* Hours Compliance Table */}

      {/* <div className="mt-6 overflow-hidden rounded-xl border border-[#E5E7EB]">

        <table className="w-full border-collapse">

          <thead>

            <tr className="border-b border-[#E5E7EB] bg-white">

              <th className="px-5 py-3 text-left text-[13px] font-semibold text-[#111827]">
                Day
              </th>

              <th className="px-5 py-3 text-center text-[13px] font-semibold text-[#111827]">
                Logged
              </th>

              <th className="px-5 py-3 text-center text-[13px] font-semibold text-[#111827]">
                Required
              </th>

              <th className="px-5 py-3 text-center text-[13px] font-semibold text-[#111827]">
                Status
              </th>

            </tr>

          </thead>

          <tbody>

            {tableData.map((row, index) => (

              <tr
                key={`${row.day}-${index}`}
                className={`
                  border-b
                  border-[#EEF2F7]
                  ${
                    index === tableData.length - 1
                      ? "bg-[#DBEAFE]"
                      : "bg-white hover:bg-[#F8FAFC]"
                  }
                `}
              >

                <td className="px-5 py-[10px] text-[13px] text-[#111827]">
                  {row.day}
                </td>

                <td className="px-5 py-[10px] text-center text-[13px] font-medium text-[#111827]">
                  {row.logged}
                </td>

                <td className="px-5 py-[10px] text-center text-[13px] text-[#667085]">
                  {row.required}
                </td>

                <td className="px-5 py-[10px] text-center">

                  <span
                    className="
                      inline-flex
                      items-center
                      rounded-full
                      bg-[#16A34A]
                      px-3
                      py-[3px]
                      text-[11px]
                      font-semibold
                      text-white
                    "
                  >
                    {row.status}
                  </span>

                </td>

              </tr>

            ))}

          </tbody>

        </table>

      </div> */}

    </div>

  );
};

export default ComplianceHoursChart;